import React from 'react';
import PropTypes from 'prop-types';

import displayName from 'component-lib/displayName';
import { FormattedMessage, FormattedHTMLMessage } from 'component-utilities/intl';

import { trim } from 'lodash';

import PrimaryButton from 'v-c/Buttons/PrimaryButton';
import SecondaryButton from 'v-c/Buttons/SecondaryButton';

import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import ModalButtons from '../ModalButtons';
import ModalContentLeft from '../ModalContentLeft';
import ModalContentCenter from '../ModalContentCenter';
import ModalText from '../ModalText';
import ModalInlineBanner from '../ModalInlineBanner';
import ModalCheckboxText from '../ModalCheckboxText';
import ModalButtonSpacer from '../ModalButtonSpacer';

import FormRow from '../../Forms/FormRow';
import FormItemFull from '../../Forms/FormItemFull';
import FormLabel from '../../Forms/FormLabel';
import InputFieldV2 from '../../Forms/InputFields/InputFieldV2';
import LargeCheckbox from '../../Forms/Checkboxes/LargeCheckbox';

const DELETE_ASSET_MODAL_STATUSES = {
    NORMAL: 'NORMAL',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
};

export { DELETE_ASSET_MODAL_STATUSES };

export default function DeleteAssetModal({ onDelete, onClose, status, reason, confirm }) {
    const BASE_KEY = 'components.modals.delete_asset';

    const isInPendingState = status === DELETE_ASSET_MODAL_STATUSES.PENDING;
    const isInSuccessState = status === DELETE_ASSET_MODAL_STATUSES.SUCCESS;
    const isInFailedState = status === DELETE_ASSET_MODAL_STATUSES.FAILED;

    const primaryButtonText = isInPendingState ? 'deleting' : 'delete';
    const onCloseClick = isInPendingState ? () => {} : onClose;

    return (
        <ModalContainer>
            <ModalHeader withIcon type="warning" onCloseClick={onCloseClick}>
                <FormattedMessage id={`${BASE_KEY}.title`} />
            </ModalHeader>

            {isInFailedState && (
                <ModalInlineBanner type="warning">
                    <FormattedMessage id={`${BASE_KEY}.failed`} />
                </ModalInlineBanner>
            )}

            {isInSuccessState && (
                <ModalContentCenter>
                    <ModalText>
                        <FormattedHTMLMessage id={`${BASE_KEY}.success`} />
                    </ModalText>
                </ModalContentCenter>
            )}

            {!isInSuccessState && (
                <ModalContentLeft>
                    <ModalText>
                        <FormattedHTMLMessage id={`${BASE_KEY}.upper_text`} />
                    </ModalText>
                    <FormRow>
                        <FormItemFull>
                            <FormLabel required>
                                <FormattedMessage id={`${BASE_KEY}.reason`} />
                            </FormLabel>
                            <InputFieldV2 focused status={isInPendingState ? 'disabled' : ''} {...reason} />
                        </FormItemFull>
                    </FormRow>
                    <ModalText>
                        <FormattedHTMLMessage id={`${BASE_KEY}.lower_text`} />
                        <LargeCheckbox rawChildren disabled={isInPendingState} {...confirm}>
                            <ModalCheckboxText>
                                <FormattedMessage id={`${BASE_KEY}.checkbox_text`} />
                            </ModalCheckboxText>
                        </LargeCheckbox>
                    </ModalText>
                </ModalContentLeft>
            )}

            <ModalButtons>
                {isInSuccessState && (
                    <PrimaryButton onClick={onClose} data-action="ok">
                        <FormattedMessage id={`${BASE_KEY}.buttons.ok`} />
                    </PrimaryButton>
                )}

                {!isInSuccessState && (
                    <div>
                        <PrimaryButton
                            onClick={onDelete}
                            data-action="delete-asset"
                            warning
                            loading={isInPendingState}
                            disabled={!trim(reason.value).length || !confirm.checked}
                        >
                            <FormattedMessage id={`${BASE_KEY}.buttons.${primaryButtonText}`} />
                        </PrimaryButton>
                        <ModalButtonSpacer />
                        <SecondaryButton onClick={onClose} data-action="cancel" disabled={isInPendingState}>
                            <FormattedMessage id={`${BASE_KEY}.buttons.cancel`} />
                        </SecondaryButton>
                    </div>
                )}
            </ModalButtons>
        </ModalContainer>
    );
}

DeleteAssetModal.displayName = displayName('Modals/DeleteAsseetModal');

DeleteAssetModal.propTypes = {
    onDelete: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    status: PropTypes.oneOf(Object.keys(DELETE_ASSET_MODAL_STATUSES).map(status => status))
        .isRequired,
    reason: PropTypes.shape({
        value: PropTypes.string.isRequired,
        onChange: PropTypes.func.isRequired,
    }).isRequired,
    confirm: PropTypes.shape({
        checked: PropTypes.bool.isRequired,
        onClick: PropTypes.func.isRequired,
    }).isRequired,
};
